// ============================================
// TEAM IMBALANCE VIEW - Balance analysis scatter
// ============================================

import { useMemo } from 'react';
import {
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, Cell, Legend
} from 'recharts';
import type { Employee } from '../../types/results';
import { getImbalanceScore } from '../../services/analytics';
import type { DemographicDimension } from '../../constants/demographics';
import { groupEmployeesByDemographic, getSegmentLabel, getSegmentColor } from '../../services/demographics';

interface TeamImbalanceViewProps {
    employees: Employee[];
    demographicDimension?: DemographicDimension | null;
}

export default function TeamImbalanceView({ employees, demographicDimension }: TeamImbalanceViewProps) {
    if (employees.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין נתונים להצגה</p>
            </div>
        );
    }

    const demographicGroups = useMemo(() => {
        if (!demographicDimension) return null;
        return groupEmployeesByDemographic(employees, demographicDimension);
    }, [employees, demographicDimension]);

    const scatterGroups = useMemo(() => {
        const buildData = (emps: Employee[]) => emps.map(e => {
            const values = Object.values(e.scores);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            const stdDev = getImbalanceScore(e);
            return {
                name: e.name,
                avg: parseFloat(avg.toFixed(2)),
                stdDev: parseFloat(stdDev.toFixed(2)),
            };
        });

        if (!demographicGroups) {
            return [{ key: 'all', label: 'Employees', color: '#133437', data: buildData(employees) }];
        }

        const groups: { key: string; label: string; color: string; data: any[] }[] = [];
        demographicGroups.forEach((emps, segKey) => {
            groups.push({
                key: segKey,
                label: getSegmentLabel(segKey, demographicDimension!),
                color: getSegmentColor(segKey),
                data: buildData(emps),
            });
        });
        return groups;
    }, [employees, demographicGroups, demographicDimension]);

    return (
        <div className="analytics-imbalance-container" style={{ direction: 'ltr' }}>
            <p className="analytics-imbalance-legend">
                ציר Y גבוה = פרופיל לא מאוזן (Spiky) | ציר X גבוה = חוסן גבוה
            </p>
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        type="number"
                        dataKey="avg"
                        name="ממוצע"
                        domain={[1, 10]}
                        ticks={[0, 2, 4, 6, 8, 10]}
                        label={{
                            value: 'ממוצע ציונים',
                            position: 'bottom',
                            fontSize: 12
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="stdDev"
                        name="סטיית תקן"
                        domain={[0, 'auto']}
                        label={{
                            value: 'מדד חוסר איזון',
                            angle: -90,
                            position: 'insideLeft',
                            fontSize: 12
                        }}
                    />
                    <Tooltip
                        cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                    <div style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #ebe6de',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                        direction: 'rtl',
                                        textAlign: 'right',
                                        padding: '12px',
                                        fontSize: '13px',
                                        minWidth: '180px'
                                    }}>
                                        <p style={{ fontWeight: 800, marginBottom: '6px', borderBottom: '1px solid #f4f1eb', paddingBottom: '6px' }}>{d.name}</p>
                                        <div style={{ color: '#938977', marginBottom: '2px' }}>ממוצע ציונים: <b style={{ color: '#133437' }}>{d.avg.toFixed(2)}</b></div>
                                        <div style={{ color: '#938977', marginBottom: '8px' }}>מדד חוסר איזון: <b style={{ color: '#133437' }}>{d.stdDev.toFixed(2)}</b></div>
                                        <div style={{
                                            fontWeight: 800,
                                            fontSize: '11px',
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            display: 'inline-block',
                                            backgroundColor: d.stdDev > 1.7 ? 'var(--warning-50)' : 'var(--success-50)',
                                            color: d.stdDev > 1.7 ? 'var(--warning-700)' : 'var(--success-700)'
                                        }}>
                                            {d.stdDev > 1.7 ? 'סיכון גבוה (לא מאוזן)' : 'פרופיל יציב'}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    {/* Risk Threshold Line */}
                    <ReferenceLine
                        y={1.7}
                        stroke="var(--accent-500)"
                        strokeDasharray="3 3"
                        label={{
                            value: 'סף סיכון',
                            position: 'insideTopRight',
                            fill: 'var(--accent-700)',
                            fontSize: 12
                        }}
                    />
                    {demographicDimension && (
                        <Legend
                            content={() => (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '20px',
                                    direction: 'rtl',
                                    paddingTop: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    {scatterGroups.map(group => (
                                        <div key={group.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '50%',
                                                backgroundColor: group.color,
                                                display: 'inline-block',
                                                flexShrink: 0
                                            }} />
                                            <span style={{ fontSize: '12px', color: '#5c5549', whiteSpace: 'nowrap' }}>{group.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    )}
                    {scatterGroups.map(group => (
                        <Scatter key={group.key} name={group.label} data={group.data}>
                            {group.data.map((entry, index) => (
                                <Cell
                                    key={`cell-${group.key}-${index}`}
                                    fill={demographicDimension ? group.color : (entry.stdDev > 1.7 ? '#baa37e' : '#133437')}
                                />
                            ))}
                        </Scatter>
                    ))}
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}
